import { useState } from 'react'
import AppDialog from '../app_dialog'
import { Button, Select, TextField, DatePicker, fromUtcDateValue, toUtcDateValue } from '@cinesend/ui-kit'
import { PlusCircle } from '@phosphor-icons/react'
import useStore from '../../hooks/use_store'
import { useNavigate } from 'react-router-dom'
import { indefiniteEndDate, indefiniteStartDate } from '../../constants/dates'
import { CycleStatus } from '../../constants/cycle_statuses'
import { GROUP_ASSET_TYPES } from '../../constants/group_asset_types'
import { DISABLED_REASONS } from '../../constants/disabled_reasons'
import DisabledTooltip from '../disabled_tooltip'

const AD_TYPES = [
  { value: 'warning-slate', label: 'Warning slate' },
  { value: 'preroll-ad', label: 'Preroll ad' },
  { value: 'interstitial-ad', label: 'Interstitial ad' },
]

export default function CreateAssetButton({ cycle, assetType, assetLabel }) {
  const cycleID = cycle.id
  const [createModal, setCreateModal] = useState(false)
  const [adType, setAdType] = useState(null)
  const [title, setTitle] = useState()
  const { createModel } = useStore()
  const navigate = useNavigate()
  const [startDate, setStartDate] = useState(cycle.start_date)
  const [endDate, setEndDate] = useState(null)
  const isAd = assetType === 'ad'
  // Group asset dates are derived from their children at snapshot time, so they aren't set on create
  const isGroupAsset = GROUP_ASSET_TYPES.includes(assetType)
  const showDateFields = isAd ? adType && adType !== 'warning-slate' : !isGroupAsset

  const closeModal = () => {
    setCreateModal(false)
    setAdType(null)
  }

  const selectAdType = (value) => {
    setAdType(value)
    if (value === 'warning-slate') {
      setStartDate(indefiniteStartDate)
      setEndDate(indefiniteEndDate)
      return
    }
    setStartDate(cycle.start_date)
    setEndDate(cycle.end_date)
  }

  const createAsset = () => {
    createModel.mutate(
      {
        endpoint: `cycles/${cycleID}/assets`,
        data: {
          cycle_id: cycleID,
          title,
          asset_type: assetType,
          // Group dates are recalculated from children at snapshot time; send the cycle range as a placeholder
          start_date: isGroupAsset ? cycle.start_date : startDate,
          end_date: isGroupAsset ? cycle.end_date : endDate,
          ad_type: isAd ? adType : null,
        },
      },
      {
        onSuccess: (res) => {
          setTitle('')
          closeModal()
          if (res.data.asset) {
            navigate(`${res.data.asset.id}`)
          }
        },
      }
    )
  }

  const isOpenCycle = cycle.status === CycleStatus.Open

  return (
    <>
      <DisabledTooltip reason={!isOpenCycle ? DISABLED_REASONS.cycleClosed : null}>
        <Button
          onClick={() => setCreateModal(true)}
          leadingIcon={isAd ? undefined : <PlusCircle size={20} />}
          disabled={!isOpenCycle}
        >
          {isAd ? 'Create clip' : 'Create'}
        </Button>
      </DisabledTooltip>
      {createModal && (
        <AppDialog
          title={isAd ? 'Create clip' : `Create ${(assetLabel || 'asset').toLowerCase()}`}
          onClose={closeModal}
          confirmText={isAd ? 'Create clip' : `Create ${assetLabel}`}
          onConfirm={() => {
            createAsset()
          }}
          confirmDisabled={
            !title || (isAd && !adType) || (showDateFields && (!startDate || !endDate)) || createModel.isLoading
          }
        >
          <div className='space-y-4'>
            {isAd && (
              <Select
                fullWidth
                label='Type'
                placeholder='Select type'
                value={adType}
                options={AD_TYPES}
                onValueChange={selectAdType}
              />
            )}
            <TextField
              label='Title'
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyPress={(e) => e.charCode === 13 && !!e.target.value && createAsset()}
              placeholder='Enter new title here...'
              autoFocus
            />
            {showDateFields && (
              <>
                <DatePicker
                  fullWidth
                  label='Start Date'
                  value={toUtcDateValue(startDate)}
                  onValueChange={(value) => setStartDate(fromUtcDateValue(value))}
                />
                <div
                  onClick={() => {
                    if (!endDate) {
                      setEndDate(startDate)
                    }
                  }}
                >
                  <DatePicker
                    fullWidth
                    label='End Date'
                    value={toUtcDateValue(endDate)}
                    onValueChange={(value) => setEndDate(fromUtcDateValue(value))}
                  />
                </div>
              </>
            )}
          </div>
        </AppDialog>
      )}
    </>
  )
}
